import React, { useState } from "react";
import DateRangeFilter from "reusable/datefilterrange";
import Export from "reusable/export";
import { CsvCondition, csvDateTime, deviceTimeZoneExportFields } from "config/csv";
import FailedInspection from "reusable/FailedInspection";
import SortBy from "reusable/sortby";
import { useTranslation } from 'react-i18next';

interface ToolbarProps {
  searchValue: string;
  sortValue: string;
  currentPage: number;
  onSearchChange: (value: string) => void;
  onSortChange: (value: string) => void;
  onPageChange: (value: number) => void;
  onDateRangeChange: (startDate: string, endDate: string) => void;
  onFailedInspectionChange: (value: boolean | null) => void;
  /** CSV に出す記録（表示中のもの）。ADM-11。以前は仮の1行だった。 */
  exportData: any[];
}

const InspectionToolbar: React.FC<ToolbarProps> = ({
  sortValue,
  currentPage,
  onSearchChange,
  onSortChange,
  onPageChange,
  onDateRangeChange,
  onFailedInspectionChange,
  exportData,
}) => {
  const { t, i18n } = useTranslation('common');
  const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
  const [failedOnly, setFailedOnly] = useState<boolean | null>(null);

  const handleFailedInspectionChange = (value: boolean | null) => {
    setFailedOnly(value);
    onFailedInspectionChange(value);
  };

  const sortingOptions = [
    { label: t('sort.default'), value: "" },
    { label: t('sort.plate-number'), value: "vehicle__plate_number" },
  ];

  const handleSortChange = (value: string) => {
    onSortChange(value);
    onPageChange(1); 
  };

  const handleDateRangeChange = (startDate: string, endDate: string) => {
    setDateRange({ startDate, endDate });
    onDateRangeChange(startDate, endDate); 
  };

  // 車両点検の記録の項目。測定値・測定時刻（data / testing_time）はこの記録に無い。
  const exportFields = [
    // ADM-11-AC1。行を画面・API の記録へたどれるよう、記録の ID を先頭に出す。
    { header: 'Record ID', field: 'id' },
    // ADM-05。作成日時は日本時間で出す（UTC の ISO 文字列のままだった）。
    { header: 'Date & Time', field: 'create_time', customExport: csvDateTime },
    { header: 'Plate Number', field: 'vehicle.plate_number' },
    { header: 'Driver Name', field: 'user.name' },
    { header: 'Maintenance Manager', field: 'maintenance_manager' },
    { header: 'Operations Manager', field: 'operations_manager' },
    // EV-116 段階3。送信の時点の端末の時間帯（無い記録は空欄）。
    ...deviceTimeZoneExportFields(),
  ];
  // ADM-11-AC2。出力した条件をファイル名に入れる（表示中のページを出す）。
  const exportConditions: CsvCondition[] = [
    ['period', dateRange.startDate && dateRange.endDate ? `${dateRange.startDate}_${dateRange.endDate}` : ''],
    ['sort', sortValue],
    ['failed', failedOnly === null ? '' : String(failedOnly)],
    ['page', currentPage],
  ];

  return (
    <section className="flex flex-row justify-between">
      <div className="flex w-1/4">
        <DateRangeFilter onDateRangeChange={handleDateRangeChange} position="right" />
      </div>
      <div className="flex flex-row justify-center items-center space-x-2">
        <SortBy
          options={sortingOptions}
          defaultValue={sortValue}
          onChange={handleSortChange}
        />
        <FailedInspection onChange={handleFailedInspectionChange} />
        <Export
          data={exportData}
          exportFields={exportFields}
          name="vehicle_inspections"
          conditions={exportConditions}
        />
      </div>
    </section>
  );
}

export default InspectionToolbar;